import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import './ReassignDialog.scss'
import {
    LuxButton,
    LuxIcon,
    LuxIconSpinner,
    LuxSelect,
    LuxAutocomplete,
    Color,
} from '@lux/wardrobe-lib'
import { ProjectsService } from '@/app/services/ProjectsService'
import { DelegateHistory, DelegateType, TypeReassignment } from '@/app/models/assignee'
import { connect } from 'react-redux'
import { getAssignees } from './../../../redux/actions/activity-execution'
import { NEW_ACTIVITY_EXECUTION_NAMESPACE } from './../../../redux/reducers/activity-execution'

export interface ReassignStoreProps {
    id: string
    name: string
    reassignRole?: string
    assigneeName?: string
    reassignmentType?: TypeReassignment
    delegateType?: DelegateType
}

interface ReassignStoreTasksProps {
    storeProps: ReassignStoreProps
    setStore: (store: ReassignStoreProps) => void
    projectDetails: any
    getAssignees: (
        storeId: string,
        role: { code: string; isApproval: boolean; search?: string },
        callback: () => void
    ) => void
    assigneesList: any[]
    isApproval: boolean
}

const ReassignStoreTasks = ({
    storeProps,
    setStore,
    projectDetails,
    getAssignees,
    assigneesList,
    isApproval,
}: ReassignStoreTasksProps) => {
    const [showReassignmentHistory, setShowReassignmentHistory] = useState<boolean>(false)
    const [assigneeLoading, setAssigneeLoading] = useState<boolean>(false)
    const [rolesList, setRolesList] = useState<{ value: string; label: string; code: string }[]>([])
    const [reassignmentHistory, setReassignmentHistory] = useState<DelegateHistory>({})
    const [showReassignmentHistoryButton, setShowReassignmentHistoryButton] =
        useState<boolean>(false)
    const [assigneePredictions, setAssigneePredictions] = useState([])
    const [autocompleteAssigneeValue, setAutocompleteAssigneeValue] = useState('')

    const intl = useIntl()

    useEffect(() => {
        ProjectsService.GetDelegateHistory({
            projectId: projectDetails._id,
            storeId: storeProps.id,
            type: storeProps.delegateType,
        })
            .then((res) => {
                setReassignmentHistory(res.data)
            })
            .catch((e) => {
                console.error(e)
            })
    }, [])

    useEffect(() => {
        if (Object.keys(reassignmentHistory).length > 0) {
            Object.keys(reassignmentHistory).forEach((key) => {
                if (reassignmentHistory[key].length > 0) {
                    setShowReassignmentHistoryButton(true)
                }
            })
        }
    }, [reassignmentHistory])
    useEffect(() => {
        setAssigneePredictions(
            assigneesList
                .sort((a, b) =>
                    (a.nickname ? a.nickname : a.lastname).toLowerCase() <
                    (b.nickname ? b.nickname : b.lastname).toLowerCase()
                        ? -1
                        : 1
                )
                .map((a) => ({
                    displayLabel: a.nickname ? a.nickname : `${a.firstname} ${a.lastname}`,
                    userId: a?.userId,
                }))
        )
    }, [assigneesList])

    useEffect(() => {
        if (isApproval) {
            ProjectsService.GetApproversList(
                projectDetails.childrenSubType || projectDetails.type
            ).then((res) => {
                if (res.data?.roleApprovers) {
                    setRolesList(
                        res.data?.roleApprovers.map((a) => ({
                            label: `${a.role} - ${a.name}`,
                            value: a.role,
                            code: a.role,
                        }))
                    )
                }
            })
            return
        }
        ProjectsService.GetReassignExecutorRoleList(
            projectDetails.type,
            projectDetails.mysteryVisit
        ).then((res) => {
            setRolesList(
                res.data?.profiles.map((p: string) => ({
                    value: p,
                    label: intl.formatMessage({
                        id: `modals.filters.visibility.${p}`,
                        defaultMessage: p,
                    }),
                    code: p,
                }))
            )
        })
    }, [projectDetails])

    const onSearchAutocompleteStore = (val: string) => {
        setAssigneeLoading(true)
        getAssignees(
            storeProps.id,
            { isApproval: isApproval, code: storeProps.reassignRole, search: val },
            () => setAssigneeLoading(false)
        )
    }

    return (
        <div>
            <LuxSelect
                value={storeProps.reassignRole}
                label={intl.formatMessage({
                    defaultMessage: 'Select role reassignment',
                    id: 'ph2.modals.reassign_dialog.select_role_reassignment',
                })}
                onChangeCallback={(v) => setStore({ ...storeProps, reassignRole: v })}
                mandatory
                options={rolesList}
            />
            {showReassignmentHistoryButton && (
                <LuxButton
                    text={intl.formatMessage({
                        defaultMessage: 'Reassignment history',
                        id: 'ph2.modals.reassign_dialog.reassignment_history',
                    })}
                    iconLeft="clock"
                    variant="text"
                    color={Color.BLUE}
                    style={{ textTransform: 'capitalize' }}
                    onClick={() => setShowReassignmentHistory(!showReassignmentHistory)}
                />
            )}
            {showReassignmentHistory && (
                <div className="delegate-history-panel">
                    {Object.keys(reassignmentHistory).length > 0 ? (
                        Object.keys(reassignmentHistory).map((key) => {
                            let firstAssignment = rolesList.find(
                                (r) => r.value === projectDetails.assignment[0]
                            )
                            return reassignmentHistory[key].length > 0 ? (
                                <div key={key}>
                                    <div className="delegate-history-panel_title">
                                        {key !== 'data'
                                            ? intl.formatMessage({
                                                  id: `ph2.modals.delegate.label.${key}`,
                                                  defaultMessage: key,
                                              })
                                            : ''}
                                    </div>
                                    <div className="flex align-items-center justify-content-between">
                                        <span>
                                            {intl.formatMessage({
                                                id: `ph2.modals.reassign_dialog.reassignment_history.first.${projectDetails.assignment[0]}`,
                                                defaultMessage: `A ${firstAssignment?.label} was first assigned`,
                                            })}
                                        </span>
                                        <span>
                                            <LuxIcon
                                                color={Color.BLUE}
                                                icon="arrow-right"
                                                size={14}
                                            />
                                            <span className="ml-2 delegate-history-panel_title">
                                                {firstAssignment?.code}
                                            </span>
                                        </span>
                                    </div>
                                    {reassignmentHistory[key].map((h) => {
                                        let assignment = rolesList.find(
                                            (r) => r.value === h.reassignment
                                        )
                                        return assignment ? (
                                            <div key={h.id}>
                                                <div className="flex align-items-center justify-content-between">
                                                    <span>
                                                        {intl.formatMessage({
                                                            id: `ph2.modals.reassign_dialog.reassignment_history.reassign.${assignment.code}`,
                                                            defaultMessage: `A ${assignment.label} was reassigned`,
                                                        })}
                                                    </span>
                                                    <span>
                                                        <LuxIcon
                                                            color={Color.BLUE}
                                                            icon="arrow-right"
                                                            size={14}
                                                        />
                                                        <span className="ml-2 delegate-history-panel_title">
                                                            {assignment?.code}
                                                        </span>
                                                    </span>
                                                </div>
                                            </div>
                                        ) : (
                                            <></>
                                        )
                                    })}
                                </div>
                            ) : (
                                <></>
                            )
                        })
                    ) : (
                        <LuxIconSpinner color={Color.BLUE} />
                    )}
                </div>
            )}
            <LuxAutocomplete
                label={intl.formatMessage({
                    defaultMessage: 'Select assignee',
                    id: 'ph2.modals.reassign_dialog.select_assignee',
                })}
                classes="m-2"
                value={autocompleteAssigneeValue}
                predictions={assigneePredictions}
                minDigit={2}
                additionalProps={{
                    appendTo: 'self',
                }}
                isLoading={assigneeLoading}
                onChangeCallback={(value) => {
                    setAutocompleteAssigneeValue(value)
                }}
                onClear={() => setStore({ ...storeProps, assigneeName: '' })}
                onSearchPredictions={onSearchAutocompleteStore}
                disabled={!storeProps.reassignRole}
                onPredictionSelected={(v) => {
                    setStore({ ...storeProps, assigneeName: v.userId })
                    setAutocompleteAssigneeValue(v.displayLabel)
                }}
                itemTemplate={(item: { displayLabel: string; userId: string }) => item.displayLabel}
            />
            <LuxSelect
                value={storeProps.reassignmentType}
                label={intl.formatMessage({
                    defaultMessage: 'Select type of reassignment',
                    id: 'ph2.modals.reassign_dialog.select_type_reassignment',
                })}
                onChangeCallback={(v) => setStore({ ...storeProps, reassignmentType: v })}
                options={[
                    {
                        label: intl.formatMessage({
                            id: 'ph2.modals.reassign_dialog.cumulative',
                            defaultMessage: 'Cumulative',
                        }),
                        value: TypeReassignment.cumulative,
                    },
                    {
                        label: intl.formatMessage({
                            id: 'ph2.modals.reassign_dialog.substitutive',
                            defaultMessage: 'Substitutive',
                        }),
                        value: TypeReassignment.substitutive,
                    },
                ]}
            />
        </div>
    )
}

const mapStateToProps = (state: any) => ({
    isApproval: state[NEW_ACTIVITY_EXECUTION_NAMESPACE].isApproval,
    assigneesList: state[NEW_ACTIVITY_EXECUTION_NAMESPACE].assignees,
})

const mapDispatchToProps = (dispatch: any) => ({
    getAssignees: (
        storeId: string,
        role: { isApproval: boolean; code: string },
        callback: () => void
    ) => {
        dispatch(getAssignees(storeId, null, role, callback))
    },
})

export default connect(mapStateToProps, mapDispatchToProps)(ReassignStoreTasks)
